import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";


const ContactFormSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  nationality: z.string().min(1, "Please select your nationality"),
  maritalStatus: z.string().min(1, "Please select marital status"),
  occupation: z.string().optional(),
  destinationCountry: z.string().min(1, "Please select a destination country"),
  levelOfStudy: z.string().optional(),
  message: z.string().optional(),
});

export default function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phone, setPhone] = useState("");
  // const [setCountries] = useState([]);
  // const [setMaritalStatuses] = useState([]);

  const form = useForm({
    resolver: zodResolver(ContactFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "", // Renamed from mobileNumber to phone for consistency
      message: "",
      maritalStatus: "",
      nationality: "",
      occupation: "",
      destinationCountry: "",
    },
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get("https://visaworlds.net/api/getData.php");
        if (response.status === 200 && response.data) {
          // setCountries(response.data.countries || []);
          // setMaritalStatuses(response.data.maritalStatuses || []);
          console.log('form submitted')
        } else {
          toast.error("Failed to fetch form data.");
        }
      } catch (error) {
        console.error("Error fetching form data:", error);
        toast.error("Error loading form options.");
      }
    };
    fetchData();
  }, []);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const payload = { ...data, phone: phone }; // Ensure correct mapping
      console.log(payload);
      const response = await axios.post("https://visaworlds.net/api/send-email.php", payload);

      if (response) {
        toast.success("Form submitted successfully!");
        form.reset();
        setPhone("");
      } else {
        toast.error(response.data.message || "Failed to submit form!");
      }
    } catch (error) {
      console.error("Form Submission Failed: ", error);
      toast.error("Failed to submit form!");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="mx-auto max-w-4xl mt-20 rounded-lg border bg-white p-8 shadow-lg">
        <h2 className="mb-6 text-center text-2xl font-bold text-gray-800">Contact Form</h2>
        <form onSubmit={form.handleSubmit(onSubmit)} className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* First Name */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">First Name</label>
            <input
              {...form.register("firstName")}
              type="text"
              placeholder="First Name"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {form.formState.errors.firstName && (
              <span className="text-sm text-red-500">{form.formState.errors.firstName.message}</span>
            )}
          </div>

          {/* Last Name */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">Last Name</label>
            <input
              {...form.register("lastName")}
              type="text"
              placeholder="Last Name"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {form.formState.errors.lastName && (
              <span className="text-sm text-red-500">{form.formState.errors.lastName.message}</span>
            )}
          </div>

          {/* Marital Status */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">Marital Status</label>
            <select
              {...form.register("maritalStatus")}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Select</option>
              <option value="Single">Single</option>
              <option value="Married">Married</option>
            </select>
            {form.formState.errors.maritalStatus && (
              <span className="text-sm text-red-500">{form.formState.errors.maritalStatus.message}</span>
            )}
          </div>

          {/* Email */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <input
              {...form.register("email")}
              type="email"
              placeholder="Email"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {form.formState.errors.email && (
              <span className="text-sm text-red-500">{form.formState.errors.email.message}</span>
            )}
          </div>

          {/* Phone */}
          <div className="col-span-1">
            <label className="mt-1 block text-sm font-medium text-gray-700">Phone</label>
            <PhoneInput
              country="in"
              value={phone}
              onChange={(value) => setPhone(value)}
              inputClass="mt-1 block w-full rounded-md border border-gray-300 p-5 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {form.formState.errors.phone && (
              <span className="text-sm text-red-500">{form.formState.errors.phone.message}</span>
            )}
          </div>

          {/* Message */}
          <div className="col-span-1 md:col-span-3">
            <label className="block text-sm font-medium text-gray-700">Message</label>
            <textarea
              {...form.register("message")}
              rows={4}
              placeholder="Message"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            ></textarea>
            {form.formState.errors.message && (
              <span className="text-sm text-red-500">{form.formState.errors.message.message}</span>
            )}
          </div>

          {/* Nationality */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">Nationality</label>
            <select
              {...form.register("nationality")}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Select</option>
              <option value="United States">USA</option>
              <option value="Canada">Denmark</option>
              <option value="Canada">Canada</option>
              <option value="Australia">Australia</option>
              <option value="UK">UK</option>
              <option value="New Zealand">New Zealand</option>
            </select>
            {form.formState.errors.nationality && (
              <span className="text-sm text-red-500">{form.formState.errors.nationality.message}</span>
            )}
          </div>

          {/* Occupation */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">Occupation</label>
            <input
              {...form.register("occupation")}
              type="text"
              placeholder="Occupation"
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            {form.formState.errors.occupation && (
              <span className="text-sm text-red-500">{form.formState.errors.occupation.message}</span>
            )}
          </div>

          {/* Destination Country */}
          <div className="col-span-1">
            <label className="block text-sm font-medium text-gray-700">Destination Country</label>
            <select
              {...form.register("destinationCountry")}
              className="mt-1 block w-full rounded-md border border-gray-300 p-2 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Select</option>
              <option value="United States">USA</option>
              <option value="Canada">Denmark</option>
              <option value="Canada">Canada</option>
              <option value="Australia">Australia</option>
              <option value="UK">UK</option>
              <option value="New Zealand">New Zealand</option>
            </select>
            {form.formState.errors.destinationCountry && (
              <span className="text-sm text-red-500">{form.formState.errors.destinationCountry.message}</span>
            )}
          </div>

          {/* Submit Button */}
          <div className="col-span-1 text-right md:col-span-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center rounded-md bg-red-500 px-6 py-2 font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
            >
              {isSubmitting ? <span>Submitting...</span> : "Apply Now"}
            </button>
          </div>
        </form>
      </div>
    
    </>
  );
}
